import { useEffect, useRef, useState, type ReactNode } from "react";

type LazySectionProps = {
  children: ReactNode;
  fallback?: ReactNode;
  rootMargin?: string;
};

const LazySection = ({
  children,
  fallback,
  rootMargin = "300px",
}: LazySectionProps) => {
  const [shouldRender, setShouldRender] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (shouldRender) {
      return;
    }

    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    const node = containerRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, shouldRender]);

  return (
    <div ref={containerRef}>
      {shouldRender ? (
        children
      ) : fallback ?? (
        <section className="py-12 lg:py-20">
          <div className="container mx-auto px-4">
            <div className="space-y-4 rounded-3xl bg-white/5 p-6 backdrop-blur-sm animate-pulse">
              <div className="h-6 w-32 rounded-full bg-white/20" />
              <div className="space-y-2">
                <div className="h-4 w-full rounded-full bg-white/10" />
                <div className="h-4 w-11/12 rounded-full bg-white/10" />
                <div className="h-4 w-9/12 rounded-full bg-white/10" />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LazySection;
